import { ReactNode } from 'react';
import { Box, CircularProgress, Divider, Link, Typography, useTheme } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { pinkLink } from './linkColor';
import {
  FINISHED,
  TEAM_ID,
  UnitedFixtures,
  kickOff,
  resultFor,
} from './useUnitedFixtures';

// The free TheSportsDB key only serves one next/last event, so the full season
// lives off-site.
const FIXTURES_URL =
  'https://www.bbc.co.uk/sport/football/teams/manchester-united/scores-fixtures';

const shortLeague = (league: string) =>
  league.replace(/^(UEFA|English|FA)\s+/, '');

const dayLabel = (date: Date) => {
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

export const OUTCOME_STYLE = {
  dark: {
    W: { bgcolor: 'rgba(34,197,94,0.22)', color: '#4ade80' },
    D: { bgcolor: 'rgba(250,204,21,0.2)', color: '#facc15' },
    L: { bgcolor: 'rgba(239,68,68,0.22)', color: '#f87171' },
  },
  light: {
    W: { bgcolor: 'rgba(34,197,94,0.16)', color: '#15803d' },
    D: { bgcolor: 'rgba(234,179,8,0.18)', color: '#a16207' },
    L: { bgcolor: 'rgba(239,68,68,0.14)', color: '#b91c1c' },
  },
};

const Team = ({ name, badge }: { name: string; badge: string | null }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.5,
      minWidth: 0,
    }}
  >
    {badge ? (
      <Box
        component="img"
        src={badge}
        alt=""
        sx={{ width: 32, height: 32, objectFit: 'contain' }}
      />
    ) : (
      <Box sx={{ width: 32, height: 32 }} />
    )}
    <Typography variant="body2" fontWeight={700} align="center" sx={{ fontSize: 13, lineHeight: 1.25 }}>
      {name}
    </Typography>
  </Box>
);

export const Pill = ({ children, sx }: { children: ReactNode; sx: object }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      height: 22,
      px: 1,
      borderRadius: 11,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 0.3,
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const LiveDot = () => (
  <Box
    component="span"
    sx={{
      width: 7,
      height: 7,
      borderRadius: '50%',
      bgcolor: '#fff',
      animation: 'livePulse 1.4s ease-in-out infinite',
      '@keyframes livePulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.35 },
      },
    }}
  />
);

const FootballCard = ({ next, last, loading, liveLabel }: UnitedFixtures) => {
  const outcomeStyle = OUTCOME_STYLE[useTheme().palette.mode];

  const title = (
    <Typography variant="h6" color="primary" gutterBottom>
      Man United
    </Typography>
  );

  const fixturesLink = (
    <Box sx={{ mt: 'auto', pt: 1, textAlign: 'right' }}>
      <Link
        href={FIXTURES_URL}
        target="_blank"
        rel="noopener noreferrer"
        variant="caption"
        underline="hover"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          fontWeight: 700,
          color: pinkLink,
        }}
      >
        All fixtures
        <ArrowOutwardIcon sx={{ fontSize: 14 }} />
      </Link>
    </Box>
  );

  if (loading) {
    return (
      <>
        {title}
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      </>
    );
  }

  if (!next && !last) {
    return (
      <>
        {title}
        <Typography variant="body2" color="text.secondary">
          Fixture info is unavailable right now.
        </Typography>
        {fixturesLink}
      </>
    );
  }

  const nextFinished = next?.strStatus && FINISHED.includes(next.strStatus);
  const upcoming = next && !nextFinished ? next : null;
  const recent = nextFinished ? next : last;

  return (
    <>
      {title}

      {upcoming && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}
          >
            {shortLeague(upcoming.strLeague)}
            {upcoming.intRound ? ` · Round ${upcoming.intRound}` : ''}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Team name={upcoming.strHomeTeam} badge={upcoming.strHomeTeamBadge} />

            {liveLabel ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Pill sx={{ bgcolor: '#dc2626', color: '#fff' }}>
                  <LiveDot />
                  LIVE · {liveLabel}
                </Pill>
                <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1, letterSpacing: 1 }}>
                  {upcoming.intHomeScore ?? 0} – {upcoming.intAwayScore ?? 0}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
                >
                  {dayLabel(kickOff(upcoming))}
                </Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>
                  {upcoming.strStatus === 'PST'
                    ? 'TBC'
                    : kickOff(upcoming).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {upcoming.strStatus === 'PST'
                    ? 'Postponed'
                    : upcoming.idHomeTeam === TEAM_ID
                      ? 'Home'
                      : 'Away'}
                </Typography>
              </Box>
            )}

            <Team name={upcoming.strAwayTeam} badge={upcoming.strAwayTeamBadge} />
          </Box>

          {upcoming.strVenue && (
            <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 0.75 }}>
              {upcoming.strVenue}
            </Typography>
          )}
        </>
      )}

      {recent && (() => {
        const result = resultFor(recent);
        return (
          <>
            {upcoming && <Divider sx={{ my: 1.25 }} />}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Pill sx={outcomeStyle[result.outcome as keyof typeof outcomeStyle]}>
                  {result.outcome}
                </Pill>
                <Typography variant="body2" noWrap>
                  <Box component="span" fontWeight={700}>
                    {result.ours}–{result.theirs}
                  </Box>{' '}
                  v {result.opponent}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" noWrap>
                {shortLeague(recent.strLeague)} ·{' '}
                {kickOff(recent).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </Typography>
            </Box>
          </>
        );
      })()}

      {fixturesLink}
    </>
  );
};

export default FootballCard;
